import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  CalendarClock,
  CalendarRange,
  CircleAlert,
  LoaderCircle,
  RefreshCw,
  TimerReset
} from "lucide-react";

import {
  fetchProfessionalSchedule,
  type ProfessionalScheduleAvailability,
  type ProfessionalScheduleResponse
} from "../lib/professionalScheduleApi";
import "../styles/horario.css";

function formatDate(value?: string | null) {
  if (!value) {
    return "Sin limite";
  }

  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1, 12, 0, 0, 0);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, "d MMM yyyy", { locale: es });
}

function formatRange(start?: string | null, end?: string | null) {
  if (!start || !end) {
    return "Descanso";
  }
  return `${start} - ${end}`;
}

function formatLeaveRange(item: ProfessionalScheduleResponse["upcoming_leaves"][number]) {
  const sameDay = item.start_date === item.end_date;
  const dateLabel = sameDay
    ? formatDate(item.start_date)
    : `${formatDate(item.start_date)} - ${formatDate(item.end_date)}`;

  if (Number(item.not_available_this_day) === 1) {
    return `${dateLabel} · Dia completo`;
  }

  return `${dateLabel} · ${formatRange(item.start_time, item.end_time)}`;
}

function normalizeError(error: unknown, fallback: string) {
  if (typeof error === "object" && error && "response" in error) {
    const responseError = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
    if (responseError) {
      return responseError;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function countWeeklyHours(availability: ProfessionalScheduleAvailability[]) {
  return availability.reduce((total, row) => {
    const ranges = [
      [row.morning_start_time, row.morning_end_time],
      [row.afternoon_start_time, row.afternoon_end_time]
    ];

    return total + ranges.reduce((carry, [start, end]) => {
      if (!start || !end) {
        return carry;
      }

      const [startHours, startMinutes] = start.split(":").map(Number);
      const [endHours, endMinutes] = end.split(":").map(Number);
      const minutes = endHours * 60 + endMinutes - (startHours * 60 + startMinutes);
      return carry + Math.max(minutes, 0) / 60;
    }, 0);
  }, 0);
}

export default function HorarioPage() {
  const [data, setData] = useState<ProfessionalScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchProfessionalSchedule();
      setData(response);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo cargar tu horario"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const weeklyHours = useMemo(() => countWeeklyHours(data?.availability || []), [data?.availability]);

  return (
    <main className="shell-page">
      <section className="sch-hero shell-hero-card">
        <div className="shell-hero-icon">
          <CalendarClock size={22} />
        </div>
        <div className="shell-hero-copy">
          <span className="shell-eyebrow">RRHH personal</span>
          <h1>Mi horario</h1>
          <p>Consulta tu disponibilidad operativa, la planificacion RRHH activa y las proximas ausencias o cierres que afectan a tu agenda.</p>
        </div>
      </section>

      <section className="sch-stats-grid">
        <article className="shell-status-card">
          <span className="shell-card-label">Bloques operativos</span>
          <strong>{data?.summary.availability_blocks ?? 0}</strong>
          <p>Tramos reales publicados en reservas para tu semana actual.</p>
        </article>
        <article className="shell-status-card">
          <span className="shell-card-label">Dias con servicio</span>
          <strong>{data?.summary.working_days ?? 0}</strong>
          <p>Dias en los que tienes al menos una franja activa.</p>
        </article>
        <article className="shell-status-card">
          <span className="shell-card-label">Horas semanales</span>
          <strong>{weeklyHours.toFixed(1)} h</strong>
          <p>Suma aproximada de los tramos visibles en tu disponibilidad.</p>
        </article>
        <article className="shell-status-card">
          <span className="shell-card-label">Plantillas RRHH</span>
          <strong>{data?.summary.active_templates ?? 0}</strong>
          <p>Planificaciones activas asignadas desde el panel admin.</p>
        </article>
      </section>

      <section className="sch-layout">
        <article className="sch-panel">
          <div className="sch-panel-header">
            <div>
              <span className="sch-panel-kicker">Disponibilidad</span>
              <h2>Horario operativo</h2>
            </div>
            <button type="button" className="sch-refresh-button" onClick={() => void loadData()}>
              <RefreshCw size={16} />
              Actualizar
            </button>
          </div>

          {error ? (
            <div className="sch-error-box">
              <CircleAlert size={18} />
              <span>{error}</span>
            </div>
          ) : null}

          {loading ? (
            <div className="sch-grid">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="sch-skeleton" />
              ))}
            </div>
          ) : !data?.availability.length ? (
            <div className="sch-empty-state">No tienes disponibilidad configurada todavia.</div>
          ) : (
            <div className="sch-grid">
              {data.availability.map((row) => (
                <article key={row.coach_availability_id} className="sch-day-card">
                  <div className="sch-day-top">
                    <div>
                      <strong>{row.day_label}</strong>
                      <div className="sch-day-meta">{row.center_name || "Sin centro asignado"}</div>
                    </div>
                    <span className="sch-chip">
                      {formatDate(row.activacion)} - {formatDate(row.desactivacion)}
                    </span>
                  </div>

                  <div className="sch-day-blocks">
                    <div className="sch-time-block">
                      <span className="sch-time-label">Manana</span>
                      <strong>{formatRange(row.morning_start_time, row.morning_end_time)}</strong>
                      <p>{row.service_name_morning || "Sin servicio"}</p>
                      <span>Capacidad {row.capacity_morning ?? "-"}</span>
                    </div>
                    <div className="sch-time-block">
                      <span className="sch-time-label">Tarde</span>
                      <strong>{formatRange(row.afternoon_start_time, row.afternoon_end_time)}</strong>
                      <p>{row.service_name_afternoon || "Sin servicio"}</p>
                      <span>Capacidad {row.capacity_afternoon ?? "-"}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </article>

        <article className="sch-panel">
          <div className="sch-panel-header">
            <div>
              <span className="sch-panel-kicker">Planificacion</span>
              <h2>Plantillas RRHH</h2>
            </div>
            <div className="sch-counter-pill">
              <CalendarRange size={16} />
              {data?.schedules.length ?? 0} plantillas
            </div>
          </div>

          {loading ? (
            <div className="sch-list">
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="sch-skeleton is-short" />
              ))}
            </div>
          ) : !data?.schedules.length ? (
            <div className="sch-empty-state">Todavia no tienes una planificacion RRHH asignada.</div>
          ) : (
            <div className="sch-list">
              {data.schedules.map((schedule) => (
                <article key={schedule.rrhh_schedule_template_id} className="sch-schedule-card">
                  <div className="sch-schedule-top">
                    <div>
                      <strong>{schedule.title}</strong>
                      <div className="sch-day-meta">{schedule.center_name || "Centro por defecto"}</div>
                    </div>
                    <span className={`sch-status-pill ${String(schedule.status).toLowerCase() === "active" ? "active" : ""}`}>
                      {schedule.status}
                    </span>
                  </div>

                  <div className="sch-schedule-validity">
                    Valida {formatDate(schedule.valid_from)} - {formatDate(schedule.valid_to)}
                  </div>

                  <div className="sch-slot-list">
                    {schedule.slots.map((slot) => (
                      <div
                        key={`${schedule.rrhh_schedule_template_id}-${slot.rrhh_schedule_template_slot_id}`}
                        className="sch-slot-item"
                      >
                        <div>
                          <strong>{slot.weekday_label}</strong>
                          <p>{slot.service_name || "Sin servicio"}</p>
                        </div>
                        <div className="sch-slot-meta">
                          <span>{slot.start_time} - {slot.end_time}</span>
                          <span>Cap. {slot.capacity ?? "-"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          )}
        </article>
      </section>

      <section className="sch-layout">
        <article className="sch-panel">
          <div className="sch-panel-header">
            <div>
              <span className="sch-panel-kicker">Ausencias aprobadas</span>
              <h2>Proximas ausencias</h2>
            </div>
            <div className="sch-counter-pill">
              <TimerReset size={16} />
              {data?.summary.upcoming_leaves ?? 0} proximas
            </div>
          </div>

          {loading ? (
            <div className="sch-list">
              <div className="sch-skeleton is-short" />
            </div>
          ) : !data?.upcoming_leaves.length ? (
            <div className="sch-empty-state">No tienes ausencias aprobadas en las proximas fechas.</div>
          ) : (
            <div className="sch-list">
              {data.upcoming_leaves.map((item) => (
                <article key={item.leave_id} className="sch-info-card">
                  <strong>{item.type_name || item.title}</strong>
                  <p>{formatLeaveRange(item)}</p>
                </article>
              ))}
            </div>
          )}
        </article>

        <article className="sch-panel">
          <div className="sch-panel-header">
            <div>
              <span className="sch-panel-kicker">Centro</span>
              <h2>Cierres y festivos</h2>
            </div>
            <div className="sch-counter-pill">
              <CalendarRange size={16} />
              {data?.summary.center_closures ?? 0} fechas
            </div>
          </div>

          {loading ? (
            <div className="sch-list">
              <div className="sch-skeleton is-short" />
            </div>
          ) : !data?.center_closures.length ? (
            <div className="sch-empty-state">No hay cierres o festivos proximos cargados en el centro.</div>
          ) : (
            <div className="sch-list">
              {data.center_closures.map((item) => (
                <article key={item.holiday_id} className="sch-info-card">
                  <strong>{item.holiday_name}</strong>
                  <p>{formatDate(item.date)}</p>
                </article>
              ))}
            </div>
          )}
        </article>
      </section>

      {loading && !data ? (
        <div className="sch-loading-overlay">
          <LoaderCircle size={18} className="spin" />
          Cargando horario...
        </div>
      ) : null}
    </main>
  );
}
